import {Validators} from '@angular/forms';

import {AutocompleteControl, CheckboxControl, Helper} from 'appShared/classes';
import {ColorCodesEnum, ComponentTypesEnum} from 'appShared/enums/common';
import {BaseControl, RadioGroupControl} from 'appShared/classes/controls';

import {MessagingStatusesEnum, MessagingTemplateTypesEnum} from '@spa/api';

import {InArrayValidator, WhitespaceValidator} from '@spa/validators';

import {
    MessageTemplateBodySelector,
    MessageTemplateChannelEmailSelector,
    MessageTemplateChannelPullSelector,
    MessageTemplateChannelPushSelector,
    MessageTemplateCompanySelector,
    MessageTemplateDepartmentSelector,
    MessageTemplateFieldBaseTemplateName,
    MessageTemplateFieldKeyDescription,
    MessageTemplateFieldNameBody,
    MessageTemplateFieldNameChannelEmail,
    MessageTemplateFieldNameChannelPull,
    MessageTemplateFieldNameChannelPush,
    MessageTemplateFieldNameCompany,
    MessageTemplateFieldNameDepartment,
    MessageTemplateFieldNameImportanceLevel,
    MessageTemplateFieldNamePushNotificationBody,
    MessageTemplateFieldNameStatus,
    MessageTemplateFieldNameSubject,
    MessageTemplateFieldNameTemplateName,
    MessageTemplateFieldNameTemplateType,
    MessageTemplateImportanceLevelSelector,
    MessageTemplateKeyCodeSelector,
    MessageTemplateKeyDescriptionSelector,
    MessageTemplatePushNotificationBodySelector,
    MessageTemplateStatusSelector,
    MessageTemplateSubjectSelector,
    MessageTemplateTemplateNameSelector,
    MessageTemplateTemplateTypeSelector,
} from 'appAssets/selectors';

export interface MessageTemplatesControlsInterface {
    status: RadioGroupControl;
    name: BaseControl;
    keyCode: AutocompleteControl;
    keyDescription: BaseControl;
    company: AutocompleteControl;
    subject: BaseControl;
    body: BaseControl;
    importanceLevel: CheckboxControl;
    templateType: RadioGroupControl;
    department: AutocompleteControl;
    channelEmail: CheckboxControl;
    channelPush: CheckboxControl;
    channelPull: CheckboxControl;
    pushNotificationBody: BaseControl;
}

export const PushNotificationBodyMaxLength = 256;

function prepareStatusOptions(isCreate: boolean, hasApprovePermission: boolean) {
    const options = [
        {
            label: Helper.textsHelper().getMessagingStatusName(MessagingStatusesEnum.PendingApproval),
            value: MessagingStatusesEnum.PendingApproval,
            color: ColorCodesEnum.Primary,
        },
    ];

    if (hasApprovePermission) {
        options.push(
            {
                label: Helper.textsHelper().getMessagingStatusName(MessagingStatusesEnum.Approved),
                value: MessagingStatusesEnum.Approved,
                color: ColorCodesEnum.Primary,
            }
        );
    }

    if (!isCreate && hasApprovePermission) {
        options.push({
            label: Helper.textsHelper().getMessagingStatusName(MessagingStatusesEnum.Rejected),
            value: MessagingStatusesEnum.Rejected,
            color: ColorCodesEnum.Primary,
        });
    }

    return options;
}

function prepareTemplateTypeOptions(isCreate: boolean, hasApprovePermission: boolean) {
    const options = [
        {
            label: Helper.textsHelper().getMessageTemplateTypeName(MessagingTemplateTypesEnum.New),
            value: MessagingTemplateTypesEnum.New,
            color: ColorCodesEnum.Primary,
        },
    ];

    if (isCreate && hasApprovePermission) {
        options.push(
            {
                label: Helper.textsHelper().getMessageTemplateTypeName(MessagingTemplateTypesEnum.Additional),
                value: MessagingTemplateTypesEnum.Additional,
                color: ColorCodesEnum.Primary,
            }
        );
    }

    return options;
}

export const MessageTemplatesControls = (isCreate: boolean, hasApprovePermission: boolean): MessageTemplatesControlsInterface => {
    return {
        status: new RadioGroupControl({
            name: MessageTemplateStatusSelector,
            value: null,
            placeholder: MessageTemplateFieldNameStatus,
            validators: [Validators.required, InArrayValidator([
                MessagingStatusesEnum.PendingApproval,
                MessagingStatusesEnum.Approved,
                MessagingStatusesEnum.Rejected,
            ])],
            componentType: ComponentTypesEnum.RadioGroup,
            options: prepareStatusOptions(isCreate, hasApprovePermission),
            sortIndex: 1,
            floatingLabelNever: true
        }),
        name: new BaseControl({
            name: MessageTemplateTemplateNameSelector,
            value: null,
            placeholder: MessageTemplateFieldNameTemplateName,
            validators: [WhitespaceValidator()],
            componentType: ComponentTypesEnum.Text,
            sortIndex: 2,
            floatingLabelNever: true,
        }),
        keyCode: new AutocompleteControl({
            name: MessageTemplateKeyCodeSelector,
            value: null,
            placeholder: MessageTemplateFieldBaseTemplateName,
            validators: [],
            componentType: ComponentTypesEnum.Autocomplete,
            options: [],
        }),
        keyDescription: new BaseControl({
            name: MessageTemplateKeyDescriptionSelector,
            value: null,
            placeholder: MessageTemplateFieldKeyDescription,
            validators: [],
            componentType: ComponentTypesEnum.Text,
            sortIndex: 2,
            floatingLabelNever: true,
        }),
        company: new AutocompleteControl({
            name: MessageTemplateCompanySelector,
            value: null,
            placeholder: MessageTemplateFieldNameCompany,
            validators: [],
            componentType: ComponentTypesEnum.Autocomplete,
            options: [],
        }),
        subject: new BaseControl({
            name: MessageTemplateSubjectSelector,
            value: null,
            placeholder: MessageTemplateFieldNameSubject,
            validators: [WhitespaceValidator()],
            componentType: ComponentTypesEnum.Text,
            sortIndex: 5,
            floatingLabelNever: true
        }),
        body: new BaseControl({
            name: MessageTemplateBodySelector,
            value: null,
            placeholder: MessageTemplateFieldNameBody,
            validators: [],
            componentType: ComponentTypesEnum.Textarea,
            sortIndex: 6,
            floatingLabelNever: true
        }),
        importanceLevel: new CheckboxControl({
            name: MessageTemplateImportanceLevelSelector,
            value: false,
            placeholder: MessageTemplateFieldNameImportanceLevel,
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
            hidden: true // This will be visible if selected distribution channels is PULL(not implemented yet)
        }),
        templateType: new RadioGroupControl({
            name: MessageTemplateTemplateTypeSelector,
            value: null,
            placeholder: MessageTemplateFieldNameTemplateType,
            validators: [],
            componentType: ComponentTypesEnum.RadioGroup,
            options: prepareTemplateTypeOptions(isCreate, hasApprovePermission),
            sortIndex: 3,
            floatingLabelNever: true
        }),
        department: new AutocompleteControl({
            name: MessageTemplateDepartmentSelector,
            value: null,
            placeholder: MessageTemplateFieldNameDepartment,
            validators: [],
            componentType: ComponentTypesEnum.Autocomplete,
            options: [],
        }),
        channelEmail: new CheckboxControl({
            name: MessageTemplateChannelEmailSelector,
            value: false,
            placeholder: MessageTemplateFieldNameChannelEmail,
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
            hidden: true,
        }),
        channelPush: new CheckboxControl({
            name: MessageTemplateChannelPushSelector,
            value: false,
            placeholder: MessageTemplateFieldNameChannelPush,
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
            hidden: true,
        }),
        channelPull: new CheckboxControl({
            name: MessageTemplateChannelPullSelector,
            value: false,
            placeholder: MessageTemplateFieldNameChannelPull,
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
            hidden: true,
        }),
        pushNotificationBody: new BaseControl({
            name: MessageTemplatePushNotificationBodySelector,
            value: null,
            placeholder: MessageTemplateFieldNamePushNotificationBody,
            validators: [Validators.maxLength(PushNotificationBodyMaxLength)],
            componentType: ComponentTypesEnum.Textarea,
            sortIndex: 7,
            floatingLabelNever: true
        }),
    };
};
